/**
 * DoorLabels — the uid -> label map that lets a check-in surface resolve
 * another door's/operator's `redeemedBy`/`undoneBy` uid into a human name
 * ("Tür 2 — Seiteneingang") on screen. `users/{uid}` is self-only read
 * (`@momo/types`' user.entity.ts), so a scan-only surface genuinely has no
 * other way to resolve a sibling account's uid — this is the one sanctioned
 * path around that. Pure state: each consumer's own `.sync.ts` (the door's
 * `scanner/src/door-labels/door-labels.sync.ts`, the admin
 * console's `web/src/events/console/door-labels.sync.ts`) owns the
 * one live read, scoped to the same `workspaces/{orgId}/settings/doorLabels`
 * doc and the same `canScanDoor` gate (`DoorLabels`'s own read rule).
 *
 * A uid `doorLabels` carries no entry for — an account provisioned after the
 * doorLabels doc was last written — resolves through `doorLabel` to `null`,
 * never the bare uid: a raw uid must never reach the operator's screen, and
 * `null` is the honest signal for "this account has no provisioned label
 * yet," not an error. Rendering that `null` as a localized "unknown door"
 * string is the view's job — `.state.ts`'s import allowlist carries no
 * `@momo/i18n` (`repo-structure`) — but the guarantee that a hex string
 * never leaks through is this file's.
 */
import { state } from "@mhmo91/schmancy/state";

export type DoorLabels = Readonly<Record<string, string>>;

// `null` until the labels document has answered for this organization — a
// switch returns it there — so "no label" is never read off a read that has
// not happened.
export const doorLabels = state<DoorLabels | null>(
	"scanner/door-labels",
).memory(null);

/** `null` when `doorLabels` has not answered or carries no entry for `uid` —
 *  never the uid. */
export function doorLabel(uid: string): string | null {
	return doorLabels.value?.[uid] ?? null;
}
